/**
 * Text input with a label, a hint and an error. Every input in the POS goes through this, so the
 * label association and the `aria-describedby` wiring exist exactly once.
 */

import { useId, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from './cn.js';

export type FieldSize = 'md' | 'lg';

// `size` is omitted because the native attribute (a character count) would collide with ours.
export type FieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'size'> & {
  label: string;
  hint?: string;
  error?: string | null;
  leading?: ReactNode;
  trailing?: ReactNode;
  /**
   * `lg` is for the handful of screens where a field is the whole task — sign-in, a PIN. Taller,
   * larger type, the control radius rather than the card one. Everything in a list or a dialog
   * stays `md`, or the density the tables rely on disappears.
   */
  size?: FieldSize;
};

const SIZES: Record<FieldSize, { wrapper: string; input: string }> = {
  md: { wrapper: 'rounded-md px-3', input: 'min-h-touch text-base' },
  lg: { wrapper: 'rounded-control px-4', input: 'min-h-comfortable text-lg' },
};

export function Field({
  label,
  hint,
  error,
  leading,
  trailing,
  className,
  size = 'md',
  ...rest
}: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  const invalid = typeof error === 'string' && error.length > 0;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-semibold text-content-secondary">
        {label}
      </label>

      <div
        className={cn(
          'flex items-center gap-2 border bg-surface-raised',
          'transition-[border-color,box-shadow] duration-fast ease-standard',
          // `border.strong` clears 3:1 against the page — the default divider colour does not,
          // and an input outline is non-text content under WCAG 1.4.11. Focus adds a soft halo
          // in the action's subtle tint on top of the border change, so the active field reads
          // from across a counter and not only up close.
          invalid
            ? 'border-danger'
            : 'border-line-strong focus-within:border-action focus-within:shadow-[0_0_0_3px_var(--pos-action-subtle)]',
          SIZES[size].wrapper,
        )}
      >
        {leading}
        <input
          {...rest}
          id={id}
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? errorId : hint !== undefined ? hintId : undefined}
          className={cn(
            'flex-1 bg-transparent text-content-primary outline-none',
            'placeholder:text-content-muted',
            SIZES[size].input,
            className,
          )}
        />
        {trailing}
      </div>

      {invalid ? (
        <p id={errorId} role="alert" className="text-sm font-medium text-danger-subtle-text">
          {error}
        </p>
      ) : (
        hint !== undefined && (
          <p id={hintId} className="text-sm text-content-muted">
            {hint}
          </p>
        )
      )}
    </div>
  );
}
